import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Plans = () => {
  const [activeTab, setActiveTab] = useState('pl');

  useEffect(() => {
    // AOS Animation initialization
    const elements = document.querySelectorAll('[data-aos]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aos-animate');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    elements.forEach(el => observer.observe(el));

    // Back to Top Button functionality
    const backToTop = document.getElementById("backToTop");
    if (backToTop) {
      window.onscroll = function () {
        if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
          backToTop.style.display = "block";
        } else {
          backToTop.style.display = "none";
        }
      };

      backToTop.addEventListener("click", () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }, []);

  const handleTabClick = (planType) => {
    setActiveTab(planType);
  };

  return (
    <>
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>We Offer...</h1>
          <p>Choose the perfect coverage for your unique needs</p>
        </div>
      </section>

      {/* Plans Navigation */}
      <section className="plans-nav">
        <div className="plans-tabs">
          <button 
            className={`tab-btn personal ${activeTab === 'pl' ? 'active' : ''}`}
            onClick={() => handleTabClick('pl')}
          >
            Personal Line
          </button>
          <button 
            className={`tab-btn corporate ${activeTab === 'cl' ? 'active' : ''}`}
            onClick={() => handleTabClick('cl')}
          >
            Corporate Line
          </button>
        </div>
      </section>

      {/* Sections */}
      <div id="sections">
        {/* Personal Line */}
        <section 
          id="pl" 
          className={`insurance-section ${activeTab === 'pl' ? 'active' : ''}`}
          style={{ display: activeTab === 'pl' ? 'block' : 'none' }}
        >
          <h2>Personal Line</h2>
          <p>
            Protect yourself and your family with our wide range of personal
            insurance solutions.
          </p>

          <div className="plans-grid">
            {/* Life Insurance */}
            <div className="plan-card">
              <h3>Life Insurance</h3>
              <p>Secure your family's financial future.</p>
              <ul className="plan-features">
                <li>✓ Term Plan (High coverage, low premium)</li>
                <li>✓ TULIP (Insurance + Investment)</li>
                <li>✓ Endowment Policy (Insurance + Savings)</li>
                <li>✓ Pension Plan (Savings for old life)</li>
              </ul>
            </div>

            {/* Health Insurance */}
            <div className="plan-card">
              <h3>Health Insurance</h3>
              <p>
                Coverage for medical emergencies, hospitalization, and critical
                illnesses.
              </p>
              <ul className="plan-features">
                <li>✓ Hospitalization Cover</li>
                <li>✓ Surgeries & Accidental</li>
                <li>✓ Cashless Treatment</li>
                <li>✓ High Sum Ensure</li>
                <li>✓ Any Age Eligible</li>
              </ul>
            </div>

            {/* Travel Insurance */}
            <div className="plan-card">
              <h3>Travel Insurance</h3>
              <p>Coverage during domestic & international travel.</p>
              <ul className="plan-features">
                <li>✓ Medical Emergencies Abroad</li>
                <li>✓ Baggage Loss Protection</li>
                <li>✓ Trip Delay & Cancellation Cover</li>
              </ul>
            </div>

            {/* Super Top-up */}
            <div className="plan-card">
              <h3>Super Top-up (Health)</h3>
              <p>
                High medical cover at a lower premium—kicks in after a deductible.
              </p>
              <ul className="plan-features">
                <li>✓ Large cover (₹10–50L) with small deductible</li>
                <li>✓ Family Floater options (self, spouse, kids, parents)</li>
                <li>✓ Works with existing base health policy</li>
                <li>✓ Cashless at network hospitals</li>
                <li>✓ Tax benefits under Sec 80D</li>
              </ul>
            </div>

            {/* Critical Illness */}
            <div className="plan-card">
              <h3>Critical Illness Insurance</h3>
              <p>
                Lump-sum payout on diagnosis of life-threatening illnesses,
                helping cover treatment and lifestyle needs.
              </p>
              <ul className="plan-features">
                <li>
                  ✓ Covers major illnesses like cancer, heart attack, stroke,
                  kidney failure
                </li>
                <li>
                  ✓ Lump-sum payout on first diagnosis — use for treatment or
                  recovery
                </li>
                <li>
                  ✓ Financial support for high medical costs and income loss
                </li>
                <li>✓ Flexible coverage options with add-ons available</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Corporate Line */}
        <section 
          id="cl" 
          className={`insurance-section ${activeTab === 'cl' ? 'active' : ''}`}
          style={{ display: activeTab === 'cl' ? 'block' : 'none' }}
        >
          <h2>Corporate Line</h2>
          <p>
            Comprehensive protection for businesses, employees, and organizational
            risks.
          </p>

          <div className="plans-grid">
            {/* Group Insurance */}
            <div className="plan-card">
              <h3>Group Insurance</h3>
              <p>
                Comprehensive coverage for employees, members, and organizations.
              </p>
              <ul className="plan-features">
                <li>✓ Group Health Insurance</li>
                <li>✓ Group Term Life Insurance</li>
                <li>✓ Group Accidental Insurance</li>
              </ul>
            </div>

            {/* Home & Property */}
            <div className="plan-card">
              <h3>Home & Property Insurance</h3>
              <p>Protection against fire, burglary, and natural disasters.</p>
              <ul className="plan-features">
                <li>✓ Fire & Disaster Coverage</li>
                <li>✓ Theft & Burglary Protection</li>
                <li>✓ Home & Building Safety Cover</li>
              </ul>
            </div>

            {/* Motor Insurance */}
            <div className="plan-card">
              <h3>Motor Insurance</h3>
              <p>
                Protection for vehicles against accidents, theft, and damages.
              </p>
              <ul className="plan-features">
                <li>✓ 2 Wheeler & 4 Wheeler Insurance</li>
                <li>✓ Commercial Vehicle Coverage</li>
                <li>✓ Passenger & Miscellaneous Vehicle Protection</li>
              </ul>
            </div>

            {/* Liability */}
            <div className="plan-card">
              <h3>Liability Insurance</h3>
              <p>Protects businesses from third-party legal liabilities.</p>
              <ul className="plan-features">
                <li>✓ Public Liability</li>
                <li>✓ Product Liability</li>
                <li>✓ Professional Liability</li>
                <li>✓ Doctor Liability</li>
              </ul>
            </div>

            {/* Factory Insurance */}
            <div className="plan-card">
              <h3>Factory Insurance</h3>
              <p>
                Comprehensive protection for factories, machinery, and industrial
                assets.
              </p>
              <ul className="plan-features">
                <li>✓ Coverage for fire, explosion, and natural calamities</li>
                <li>✓ Protection for plant, machinery, and raw materials</li>
                <li>✓ Safeguards against operational interruptions</li>
              </ul>
            </div>

            {/* Shop Insurance */}
            <div className="plan-card">
              <h3>Shop Insurance</h3>
              <p>
                Tailored coverage for retail shops and commercial establishments.
              </p>
              <ul className="plan-features">
                <li>✓ Fire, burglary, and theft protection</li>
                <li>✓ Coverage for stock, furniture, and equipment</li>
                <li>✓ Public liability and business interruption support</li>
              </ul>
            </div>

            {/* Workmen's Compensation */}
            <div className="plan-card">
              <h3>Workmen's Compensation</h3>
              <p>Coverage for workplace accidents or injuries.</p>
              <ul className="plan-features">
                <li>✓ Medical Expense Coverage</li>
                <li>✓ Disability & Death Benefits</li>
                <li>✓ Legal Liability Protection</li>
              </ul>
            </div>

            {/* Marine Inland Transit */}
            <div className="plan-card">
              <h3>Marine Inland Transit Insurance</h3>
              <p>
                Coverage for goods and cargo transported within India by road,
                rail, or inland waterways — with single-transit and open marine
                options.
              </p>
              <ul className="plan-features">
                <li>
                  ✓ Protection against transit risks during inland transport
                </li>
                <li>
                  ✓ Coverage for goods moved by road, rail, or inland waterways
                </li>
                <li>
                  ✓ Safeguards against accidents, theft, and unforeseen damages
                </li>
                <li>
                  ✓ <strong>Open Marine Policy</strong> option for frequent
                  shipments
                </li>
              </ul>
            </div>

            {/* Cyber Insurance */}
            <div className="plan-card">
              <h3>Cyber Insurance</h3>
              <p>
                Protection against digital threats, cyber-attacks, and data
                breaches.
              </p>
              <ul className="plan-features">
                <li>✓ Coverage for data breach and cyber-attack losses</li>
                <li>
                  ✓ Protection against ransomware, phishing, and malware threats
                </li>
                <li>✓ Legal expense and regulatory penalty coverage</li>
                <li>✓ Business interruption support after cyber incidents</li>
              </ul>
            </div>

            {/* Directors & Professionals */}
            <div className="plan-card">
              <h3>Directors & Professional Insurance</h3>
              <p>
                Protection for directors, executives, and professionals against
                legal claims.
              </p>
              <ul className="plan-features">
                <li>✓ Management Liability Cover</li>
                <li>✓ Negligence & Error Protection</li>
                <li>✓ Legal Defense Support</li>
              </ul>
            </div>

            {/* Surety Bonds */}
            <div className="plan-card">
              <h3>Surety Bonds</h3>
              <p>
                Financial guarantees that protect contractual obligations and
                performance.
              </p>
              <ul className="plan-features">
                <li>✓ Performance Bonds</li>
                <li>✓ Bid Bonds</li>
                <li>✓ Advance Payment Bonds</li>
                <li>✓ License & Permit Bonds</li>
                <li>✓ Customs Bonds</li>
                <li>✓ Court Bonds</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content" data-aos="fade-up">
            <h2>Need Help Choosing the Right Plan?</h2>
            <p>
              Our insurance experts are here to help you find the perfect coverage
              for your needs
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Get Expert Advice</Link>
              <Link to="/contact" className="btn-secondary">Call Now</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button id="backToTop" title="Go to top">↑</button>
    </>
  );
};

export default Plans;
